import { View, Text, styled } from 'tamagui'
import { Mic, MicOff, Headphones, VolumeX, PhoneOff } from '@tamagui/lucide-icons'
import { useAppStore, useServerStore } from '@/store'
import { voiceChannelParticipants, users } from '@/mocks'
import { Avatar } from '../common'

const Bar = styled(View, {
  height: 48,
  backgroundColor: '$color2',
  flexDirection: 'row',
  alignItems: 'center',
  paddingHorizontal: 16,
  borderTopWidth: 1,
  borderTopColor: '$borderDefault',
  gap: 12,
})

const VoiceInfo = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 8,
  flex: 1,
})

const VoiceDot = styled(View, {
  width: 8,
  height: 8,
  borderRadius: '$borderRadiusSm',
  backgroundColor: '$success',
  // @ts-expect-error -- web animation
  style: {
    animation: 'voicePulse 1.5s ease-in-out infinite',
  },
})

const ChannelName = styled(Text, {
  fontFamily: '$fontBody',
  fontWeight: '600',
  fontSize: 13,
  color: '$surfaceForeground',
})

const ServerLabel = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 11,
  color: '$sidebarMuted',
})

const ParticipantRow = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: -6,
})

const ControlButton = styled(View, {
  width: 32,
  height: 32,
  borderRadius: '$borderRadiusLg',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  hoverStyle: {
    backgroundColor: '$sidebarHover',
  },
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 150ms ease' },
})

const DisconnectButton = styled(View, {
  width: 32,
  height: 32,
  borderRadius: '$borderRadiusLg',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '$error',
  cursor: 'pointer',
  hoverStyle: {
    opacity: 0.85,
  },
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 150ms ease' },
})

export function VoiceBar() {
  const { voiceChannelId, isMuted, isDeafened, toggleMute, toggleDeafen, leaveVoiceChannel } = useAppStore()
  const servers = useServerStore((s) => s.servers)

  if (!voiceChannelId) return null

  // Find the server and channel for this voice channel
  let channelName = 'Voice'
  let serverName = ''
  for (const server of Object.values(servers)) {
    const channel = server.channels.find(c => c.id === voiceChannelId)
    if (channel) {
      channelName = channel.name
      serverName = server.name
      break
    }
  }

  const participants = voiceChannelParticipants[voiceChannelId] || []

  return (
    <Bar>
      <VoiceDot />

      <VoiceInfo>
        <View>
          <ChannelName>{channelName}</ChannelName>
          <ServerLabel>{serverName}</ServerLabel>
        </View>
      </VoiceInfo>

      <ParticipantRow>
        {participants.map((p) => {
          const user = users[p.userId]
          if (!user) return null
          return (
            <View key={p.userId} marginLeft={-4}>
              <Avatar
                name={user.displayName}
                color={user.roleColor || '#2A3142'}
                size="sm"
                showStatus={false}
                isSpeaking={p.isSpeaking}
              />
            </View>
          )
        })}
      </ParticipantRow>

      <View flexDirection="row" gap={4}>
        <ControlButton onPress={toggleMute}>
          {isMuted ? <MicOff size={16} color="$error" /> : <Mic size={16} color="$sidebarMuted" />}
        </ControlButton>
        <ControlButton onPress={toggleDeafen}>
          {isDeafened ? <VolumeX size={16} color="$error" /> : <Headphones size={16} color="$sidebarMuted" />}
        </ControlButton>
        <DisconnectButton onPress={leaveVoiceChannel}>
          <PhoneOff size={14} color="$surfaceForeground" />
        </DisconnectButton>
      </View>
    </Bar>
  )
}
